<template>
  <v-container
    class="px-0 py-0"
    fluid
  >
    <v-card
      v-if="items"
      class="mt-3 mx-3 bt-1 br-1 bl-1 bc-outlined-gray"
      tile
    >
      <v-card-text class="fs-15 py-0">
        <v-row dense>
          <v-col class="d-flex align-center">
            <p class="mb-0 py-4">
              {{ $bct('Displaying') }}
              <strong>{{ items?.length }}</strong> {{ $bct('out of') }}
              <strong>{{ totalItemCount.toLocaleString() }}</strong> {{ $bct('total results') }}.
            </p>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <DataTableServer
      v-if="currentPage"
      @click:row="handleRowClick"
      @update:page="handlePageChange"
      :headers="tableHeaders"
      :hide-default-footer="!totalItemCount"
      :items="items"
      :items-length="totalItemCount"
      :items-per-page="pageSize"
      :loading="processing"
      :page="currentPage"
      class="mx-3 b-radius-0 b-1 bc-outlined-gray"
      item-key="id"
      no-data-text="No statements found"
      fixed-header
      hover
      return-object
    >
      <template #[`item.id`]="{ item }">
        {{ shortId(item.id) }}
      </template>

      <template #[`item.provider_name`]="{ item }">
        <router-link
          v-if="item.provider_id"
          @click.stop.prevent
          :aria-label="$bct('Go to provider')"
          :to="{ name: 'ProviderShow', params: { providerId: item.provider_id } }"
        >
          {{ item.provider_name }}
        </router-link>
        <span v-else>
          {{ item.provider_name }}
        </span>
      </template>

      <template #[`item.amount`]="{ item }">
        <span>
          {{ currency(item.amount) }}
        </span>
      </template>

      <template #[`item.created_at`]="{ item }">
        <span>
          {{ formatDate(item.created_at) }}
        </span>
      </template>

      <template #[`item.status`]="{ item }">
        <StandardChip
          v-bind="getBillingStatementStatusPresentation(item.status)"
          :text="getBillingStatementStatusDisplayValue(item.status)"
          role="status"
        />
      </template>
    </DataTableServer>

    <VerticalSpacer :min-height="14" />

    <BillingStatementDialog
      ref="statementShowDialog"
      :api="props.statementsApi"
      :can-open-invoices="canOpenInvoices"
    />
  </v-container>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';

import BillingStatementDialog from '@/shared/components/billing/BillingStatementDialog.vue';
import { currency, shortId } from '@/plugins/filters';
import DataTableServer from '@/shared/components/table/DataTableServer.vue';
import {
  getBillingStatementStatusDisplayValue,
  getBillingStatementStatusPresentation,
} from '@/shared/utils/invoiceStatus';
import useRouterHelper from '@/shared/composables/useRouterHelper';
import VerticalSpacer from '@/shared/components/VerticalSpacer.vue';

const route = useRoute();
const router = useRouter();
const { updateQuery } = useRouterHelper();

const $bct = inject('translator');

const props = defineProps({
  canOpenInvoices: Boolean,
  openAsPage: {
    type: Boolean,
    default: false,
  },
  statementsApi: {
    type: Object,
    default: () => ({}),
  },
});

const currentPage = ref(null);
const items = ref([]);
const pageSize = ref(15);
const processing = ref(true);
const statementDates = ref([]);
const tableHeaders = ref([
  {
    title: 'Statement ID',
    value: 'id',
  },
  {
    title: 'Location',
    value: 'provider_name',
  },
  {
    title: 'Date',
    value: 'created_at',
  },
  {
    title: 'Amount',
    value: 'amount',
    align: 'end',
  },
  {
    title: 'Status',
    value: 'status',
    align: 'center',
  },
]);
const totalItemCount = ref(0);
const statementShowDialog = ref(null);

function formatDate(date) {
  if (!date) return null;

  return new Date(date).toISOString().split('T')[0];
}

async function handlePageChange(newValue) {
  if (processing.value) return;

  currentPage.value = newValue;
  await loadStatements();
}

async function load() {
  currentPage.value = route?.query.page ? parseInt(route.query.page, 10) || 1 : 1;

  await loadStatements();
}

async function handleRowClick(_event, { item }) {
  if (props.openAsPage) {
    await router.push({
      name: 'BillingStatementShow',
      params: { id: item.id },
      query: { from: currentPage.value },
    });
  } else {
    statementShowDialog.value.open(item.id);
  }
}

async function loadStatements() {
  processing.value = true;
  const response = await props.statementsApi.index({
    page: currentPage.value,
    page_size: pageSize.value,
  });
  processing.value = false;

  items.value = response.data;
  totalItemCount.value = parseInt(response?.headers?.['x-total-count'] || 0, 10);
  await updateQuery({ page: currentPage.value });

  void setStatementDates();
}

function setStatementDates() {
  statementDates.value = items.value.map((item) => item.date);
}

onMounted(load);
</script>
